// src/services/ProductService.ts
import axios from "axios";

const API_BASE = process.env.REACT_APP_BASE_URL;


export interface ProductFormData {
  name: string;
  price: string;
  description: string;
  stock: string;
  categoryId: string;
  productTypeId?: string;
  images?: FileList | null;
}

export interface ProductImage {
  id?: number;
  signedUrl: string;
  isPrimary: boolean;
  image_key: string;
}

export interface Product {
  id: string;
  name: string;
  price: string;
  description: string;
  stock_quantity: string;
  images?: {
    id?: number;
    signedUrl: string;
    isPrimary: boolean;
    image_key: string;
  }[];
  categoryId?: string;
  category?: {
    id: number;
    name: string;
    description?: string;
  };
  productTypeId?: string;
  product_type?: {
    id: number;
    name: string;
    description?: string;
  };

}


export const ProductService = {
  /** CREATE product with optional images */
  createProductWithImages: async (data: ProductFormData): Promise<void> => {
    const formData = new FormData();

    (Object.entries(data) as [keyof ProductFormData, any][]).forEach(
      ([key, value]) => {
        if (key !== "images" && typeof value === "string") {
          formData.append(key, value);
        }
      }
    );

    if (data.images && data.images.length > 0) {
      Array.from(data.images).forEach(file => {
        formData.append("images", file);
      });
    }

    const response = await axios.post(`${API_BASE}/products/with-images`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
      withCredentials: true,
    });
    return response.data;
  },

  /** READ all products */
  getAllProducts: async (): Promise<Product[]> => {
    const response = await axios.get(`${API_BASE}/products`, { withCredentials: true });
    return response.data;
  },

  getRecentProducts: async () => {
    const res = await axios.get(`${API_BASE}/products/recent-products`);
    return res.data;
  },


  /** GET products by categoryId with optional limit */
  getProductsByCategory: async (categoryId: string, limit?: number): Promise<Product[]> => {
    const params: Record<string, string> = {};
    if (limit) params.limit = limit.toString();

    const response = await axios.get(`${API_BASE}/products/category/${categoryId}`, {
      params,
      withCredentials: true,
    });

    return response.data.data;
  },

  /** READ single product by ID */
  getProductById: async (id: string): Promise<Product> => {
    const response = await axios.get(`${API_BASE}/products/${id}`, { withCredentials: true });
    return response.data;
  },

  /** UPDATE a product (basic fields only) */
  updateProduct: async (id: string, data: Partial<ProductFormData>): Promise<void> => {
    await axios.put(`${API_BASE}/products/${id}`, data, { withCredentials: true });
  },

  /** UPDATE a product with optional images */
  updateProductWithImages: async (id: string, data: Partial<ProductFormData>): Promise<void> => {
    const formData = new FormData();

    (Object.entries(data) as [keyof ProductFormData, any][]).forEach(([key, value]) => {
      if (key !== "images" && value !== undefined) {
        formData.append(key, value);
      }
    });

    if (data.images && data.images.length > 0) {
      Array.from(data.images).forEach(file => formData.append("images", file));
    }

    await axios.put(`${API_BASE}/products/${id}/with-images`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
      withCredentials: true,
    });
  },

  /** DELETE a product by ID */
  deleteProduct: async (id: string): Promise<void> => {
    await axios.delete(`${API_BASE}/products/${id}`, { withCredentials: true });
  },

  /** Checkout (example) */
  checkout: async (payload: any) => {
    const res = await axios.post(`${API_BASE}/orders`, payload, { withCredentials: true });
    return res.data;
  },

    /** FETCH products with server-side pagination & filters */
  getProducts: async ({
    page = 1,
    limit = 12,
    categoryId,
    maxPrice,
    minStock,
  }: {
    page?: number;
    limit?: number;
    categoryId?: string | number;
    maxPrice?: number;
    minStock?: number;
  }): Promise<{
    data: Product[];
    pagination: { page: number; limit: number; totalItems: number; totalPages: number };
  }> => {
    const params: any = { page, limit };
    if (categoryId) params.categoryId = categoryId;
    if (maxPrice !== undefined) params.maxPrice = maxPrice;
    if (minStock !== undefined) params.minStock = minStock;

    const res = await axios.get(`${API_BASE}/products`, { params, withCredentials: true });
    return res.data;
  },
};
